import { useCallback, useState, useEffect, Fragment } from 'react';
import clsx from 'clsx';
import Swal from 'sweetalert2';
import { Modal } from 'react-bootstrap-v5';
import moment from 'moment';
import ReactS3Client from 'react-aws-s3-typescript';
import { PageTitle, useLayout } from "../../../_metronic/layout/core";
import { DefaultTitle } from "../../../_metronic/layout/components/header/page-title/DefaultTitle";
import suporteService from '../../services/suporteService';
import { uniqueId } from 'lodash';
import { configBucket } from '../../utils/S3Utils';
import { useAuth } from '../../contexts/AuthProvider';
import { joinGroup, startConnection, closeConnection, connectionValid, removeGroup } from '../../services/EsteiraWebSocket';

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

const ModalData = ({ item, show, handleClose }: { item: any, show: boolean, handleClose: Function }) => {

    const [assunto, setAssunto] = useState<string>(item?.assunto || '');
    const [descricao, setDescricao] = useState<string>(item?.descricao || '');
    const [resposta, setResposta] = useState<string>(item?.resposta || '');
    const [status, setStatus] = useState<string>(item?.status || '');
    const [loadingFile, setLoadingFile] = useState<boolean>(false);
    const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
    const [anexos, setAnexos] = useState<string[]>(item?.anexos || []);
    const [id, setId] = useState<number>(item?.id || 0);
    const { usuario } = useAuth();
    const [mensagens, setMensagens] = useState<any[]>([]);
    const [mensagemDigitada, setMensagemDigitada] = useState<string>('');
    const [enviandoMensagem, setEnviandoMensagem] = useState<boolean>(false);
    const [carregandoMensagens, setCarregandoMensagens] = useState<boolean>(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (item) {
            setAssunto(item.assunto);
            setDescricao(item.descricao);
            setResposta(item.resposta || '');
            setStatus(item.status);
            setAnexos(item.anexos || []);
            setId(item.id || 0);

            setCarregandoMensagens(true);
            suporteService.buscarMensagens(item.id).then((resp) => {
                setCarregandoMensagens(false);
                setMensagens(resp);
            });
        } else {
            setAssunto('');
            setDescricao('');
            setResposta('');
            setStatus('');
            setAnexos([]);
            setId(0);
        }
    }, [item]);

    const enviarMensagem = useCallback(() => {
        if (!mensagemDigitada) return;

        setEnviandoMensagem(true);
        suporteService.enviarMensagem(id, mensagemDigitada).then(() => {
            setMensagens((old) => {
                const cp = [...old];
                cp.push({
                    usuarioID: usuario?.id,
                    mensagem: mensagemDigitada
                });

                return cp;
            });

            setMensagemDigitada('');
            setEnviandoMensagem(false);
        });
    }, [id, mensagemDigitada, usuario]);

    const criarTicket = useCallback(() => {
        setLoadingCreate(true);

        if (!assunto || !descricao) {
            Swal.fire({
                text: 'Por favor preencha os campos assunto e descrição',
                timer: 3000,
                icon: 'error',
                showConfirmButton: false
            });

            setLoadingCreate(false);

            return;
        }

        suporteService.inserir(assunto, descricao, anexos).then(() => {
            Swal.fire({
                text: 'Seu ticket foi aberto, entraremos em contato via whatsapp ou via chamado.',
                timer: 3000,
                icon: 'success',
                showConfirmButton: false
            });

            setDescricao('');
            setAssunto('');
            setAnexos([]);
            setStatus('');
            setResposta('');

            handleClose();
            setLoadingCreate(false);
        }).catch(() => {
            Swal.fire({
                text: 'Ocorreu um erro ao criar o ticket',
                timer: 3000,
                icon: 'error',
                showConfirmButton: false
            });

            setLoadingCreate(false);
        });
    }, [assunto, descricao, anexos, handleClose]);

    const getColor = useCallback(() => {
        if (item && item.status === 'Aberto') return 'green';
        if (item && item.status === 'Em Andamento') return 'coral';
        if (item && item.status === 'Fechado') return 'red';
    }, [item]);

    useEffect(() => {
        if (id && id > 0 && usuario) {
            const valid = async () => {
                await closeConnection();
                await startConnection();
                await removeGroup(id.toString());
                await joinGroup(id.toString());

                connectionValid.on('MensagemSistema', (args) => {
                    const data = JSON.parse(args);
                    setMensagens((old) => {
                        const cp = [...old];
                        if (data.usuarioID !== usuario.id) {
                            cp.push(data);
                        }

                        return cp;
                    });
                });

                connectionValid.on('AlterarStatus', (args) => {
                    const data = JSON.parse(args);
                    setStatus(data.status);
                });

                connectionValid.on('Anexo', (args) => {
                    const data = JSON.parse(args);
                    setAnexos((old) => {
                        return old.concat(data.url);
                    });
                });
            }

            valid();
        }
    }, [id, usuario]);

    return (
        <Modal size='lg' centered={true} show={show} onHide={() => handleClose()}>
            <div className="modal-content" style={{ backgroundColor: '#F4F5F7' }}>
                <div className="modal-header">
                    <h5 className="modal-title titulo-modal" style={{ textTransform: 'uppercase' }}>{t('Suporte')}</h5>
                    <div className="btn btn-icon btn-sm btn-active-light-primary ms-2">
                        <i onClick={() => handleClose()} className="fas fa-times" style={{ color: '#5E6397' }}></i>
                    </div>
                </div>
                <div className="modal-body">
                    <div className="row">
                        {status && status !== 'Em Andamento' && (
                            <div className='col-md-12 mt-2'>
                                <label style={{ padding: '10px', backgroundColor: getColor(), color: 'white', borderRadius: '5px' }}>{status}</label>
                            </div>
                        )}
                        <div className="col-md-12">
                            <div className='row'>
                                {status !== 'Em Andamento' && (
                                    <>
                                        <div className='col-md-12 mt-2'>
                                            <label>{t('Assunto')}</label>
                                            <div className='form-group'>
                                                <input className='form-control' value={assunto} onChange={(e) => setAssunto(e.target.value)} disabled={id > 0} />
                                            </div>
                                        </div>
                                        <div className='col-md-12 mt-2'>
                                            <label>{t('Descrição do problema')}</label>
                                            <div className='form-group'>
                                                <input className='form-control' value={descricao} onChange={(e) => setDescricao(e.target.value)} disabled={id > 0} />
                                            </div>
                                        </div>

                                    </>
                                )}

                                {id > 0 && (status === 'Em Andamento' || mensagens.length > 0) && (
                                    <div className='col-md-12 mt-2'>
                                        <div className='row'>
                                            <div className='col-md-12'>
                                                <label>Chat</label>
                                            </div>
                                            <div className='col-md-12 mt-2' style={{ borderRadius: '10px', backgroundColor: '#ecf0f1', maxHeight: '300px', overflowY: 'scroll', height: '300px', }}>
                                                <div className='row' style={{ padding: '10px' }}>
                                                    {mensagens.map((item) => (
                                                        <Fragment key={uniqueId()}>
                                                            {item.usuarioID === usuario?.id ? (
                                                                <div className='col-md-12 mt-2' style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                                    <label style={{ backgroundColor: 'white', borderRadius: '5px', padding: '5px', width: 'auto', maxWidth: '50%', wordWrap: 'break-word' }}>{item.mensagem}</label>
                                                                </div>
                                                            ) : (
                                                                <div className='col-md-12 mt-2'>
                                                                    <label style={{ textAlign: 'left', backgroundColor: 'white', borderRadius: '5px', padding: '5px', width: 'auto', maxWidth: '50%', wordWrap: 'break-word' }}>{item.mensagem}</label>
                                                                </div>
                                                            )}
                                                        </Fragment>
                                                    ))}
                                                </div>
                                            </div>
                                            {status === 'Em Andamento' && (
                                                <div className='col-md-12 mt-2'>
                                                    <div className='row'>
                                                        <div className='col-md-11 form-group'>
                                                            <input onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    enviarMensagem();
                                                                }
                                                            }} disabled={carregandoMensagens || enviandoMensagem} className='form-control' placeholder='Digite sua mensagem aqui' value={mensagemDigitada} onChange={(e) => setMensagemDigitada(e.target.value)} />
                                                        </div>
                                                        <div className='col-md-1' style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }} >
                                                            <button onClick={enviarMensagem} disabled={carregandoMensagens || enviandoMensagem} style={{ backgroundColor: 'transparent', border: 'none' }}>
                                                                <i className='fas fa-paper-plane' style={{ color: 'black', width: '20px' }} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                <div className='col-md-12 mt-2'>
                                    <label>{t('Anexos')}</label>
                                </div>

                                {loadingFile && (
                                    <div className='col-md-12 mt-2'>
                                        <span className='indicator-progress' style={{ display: 'block' }}>
                                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                            {t('Aguarde...')}
                                        </span>
                                    </div>
                                )}

                                {(id <= 0 || status === 'Em Andamento') && !loadingFile && (
                                    <div className='col-md-12 mt-2'>
                                        <input id="inputFile" type="file" onChange={async (e) => {
                                            setLoadingFile(true);
                                            if (e.target.files !== null && e.target.files.length > 0) {
                                                const file = e.target.files[0];
                                                const s3 = new ReactS3Client(configBucket);
                                                const res = await s3.uploadFile(file);
                                                setAnexos((old) => {
                                                    return old.concat(res.location);
                                                });
                                                if (id > 0) {
                                                    suporteService.anexar(id, res.location);
                                                }
                                            }
                                            setLoadingFile(false);
                                        }} />
                                    </div>
                                )}

                                {anexos.length === 0 && <div className='col-md-12 mt-2'><label>{t('Nenhum anexo encontrado')}</label></div>}

                                <div className='col-md-12 mt-2'>
                                    <ul>
                                        {anexos.map((item, idx) => (
                                            <li key={uniqueId()}>
                                                <a href={item} target="_blank">{item}</a>
                                                {id <= 0 && <button onClick={() => {
                                                    setAnexos((old) => {
                                                        let copy = [...old] || [];
                                                        copy.splice(idx, 1);
                                                        return copy;
                                                    });
                                                }} style={{ background: 'transparent', border: 'none', marginLeft: '10px' }}>{t('Remover')}</button>}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {id <= 0 && !loadingCreate && (
                                    <div className='col-md-12 mt-2'>
                                        <button onClick={() => criarTicket()} className='btn btn-default' style={{ color: 'white', backgroundColor: '#005E96' }}>{t('Criar')}</button>
                                    </div>
                                )}

                                {loadingCreate && (
                                    <div className='col-md-12 mt-2'>
                                        <span className='indicator-progress' style={{ display: 'block' }}>
                                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                            {t('Aguarde...')}
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

const Suporte = () => {
    const { t } = useTranslation();
    const { classes } = useLayout();
    const [suportes, setSuportes] = useState<any[]>();
    const [loading, setLoading] = useState<boolean>(false);
    const { usuario } = useAuth();
    const [novasMensagens, setNovasMensagens] = useState<any[]>([]);

    const [modal, setModal] = useState<any>({
        show: false,
        defaultItem: null
    });

    useEffect(() => {
        setLoading(true);
        suporteService.listar().then((resp) => {
            setSuportes(resp);
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (usuario && !modal?.show) {
            const valid = async () => {
                await closeConnection();
                await startConnection();
                await removeGroup(`Mensagem${usuario.clienteID?.toString()}`);
                await joinGroup(`Mensagem${usuario.clienteID?.toString()}`);

                connectionValid.on('Global', (args) => {
                    console.log('chego')
                    const data = JSON.parse(args);
                    setNovasMensagens((old) => {
                        const cp = [...old];
                        cp.push(parseInt(data.suporteID));
                        return cp;
                    });
                });
            }

            valid();
        }
    }, [usuario, modal?.show])

    return (<>
        <PageTitle breadcrumbs={[]}>{t('Suporte')}</PageTitle>
        <div className='toolbar' id='kt_toolbar'>
            <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                <DefaultTitle />
            </div>
        </div>
        <ModalData item={modal.defaultItem} handleClose={() => {
            setLoading(true);
            suporteService.listar().then((resp) => {
                setSuportes(resp);
                setLoading(false);
            });
            setModal({ show: false, defaultItem: null });
        }} show={modal.show} />
        <div className='row mt-5'>
            <div className='col-md-12'>
                <div className='row'>
                    <div className='col-md-9'>
                    </div>
                    <div className='col-md-3'>
                        <button type="button" style={{ width: '100%', backgroundColor: '#005E96', color: 'white' }} onClick={() => setModal({ show: true, defaultItem: null })} className='btn btn-default'>{t('Criar Ticket')}</button>
                    </div>
                </div>
                <div className='row mt-5'>
                    <div className='col-md-12'>
                        <table className="table table-striped">
                            <thead>
                                <tr>
                                    <th scope="col">{t('Código')}</th>
                                    <th scope="col">{t('Assunto')}</th>
                                    <th scope="col">{t('Data Abertura')}</th>
                                    <th scope="col">{t('Status')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {suportes?.map((item) => (
                                    <tr key={uniqueId()} onClick={() => setModal({ show: true, defaultItem: item })}>
                                        <th scope="row">{item.id}</th>
                                        <td>{item.assunto}</td>
                                        <td>{moment(item.dataAbertura).format('DD/MM/YYYY')}</td>
                                        <td>{item.status}</td>
                                        <td>{novasMensagens.find(f => f === item.id) && (<span className="badge badge-primary">{t('Novas')}</span>)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {loading && (
                        <div className='col-md-12 mt-2'>
                            <span className='indicator-progress' style={{ display: 'block' }}>
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                {t('Carregando novos dados...')}
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    </>);
}

export default Suporte;


